/**
 * Two panes showing the same sheet move together. These are the sums, kept free of the
 * DOM so they can be tested: where the follower should scroll to, and where a pane
 * should scroll to keep a point still while the zoom changes.
 */
export interface ScrollBox {
  left: number;
  top: number;
  /** Size of the content. */
  scrollWidth: number;
  scrollHeight: number;
  /** Size of the window onto it. */
  clientWidth: number;
  clientHeight: number;
}

const range = (content: number, viewport: number) => Math.max(0, content - viewport);
const fraction = (value: number, max: number) =>
  max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;

/**
 * Where `follower` should scroll so it shows the same part of the sheet as `leader`.
 * Panes of different widths show the same centre, not the same left edge.
 */
export function mirrorScroll(
  leader: ScrollBox,
  follower: Pick<ScrollBox, "scrollWidth" | "scrollHeight" | "clientWidth" | "clientHeight">,
): { left: number; top: number } {
  const cx =
    leader.scrollWidth > 0 ? (leader.left + leader.clientWidth / 2) / leader.scrollWidth : 0.5;
  const cy =
    leader.scrollHeight > 0 ? (leader.top + leader.clientHeight / 2) / leader.scrollHeight : 0.5;
  const left = cx * follower.scrollWidth - follower.clientWidth / 2;
  const top = cy * follower.scrollHeight - follower.clientHeight / 2;
  return {
    left: Math.round(
      fraction(left, range(follower.scrollWidth, follower.clientWidth)) *
        range(follower.scrollWidth, follower.clientWidth),
    ),
    top: Math.round(
      fraction(top, range(follower.scrollHeight, follower.clientHeight)) *
        range(follower.scrollHeight, follower.clientHeight),
    ),
  };
}

/**
 * The scroll position after a zoom change that keeps the sheet point under `anchor`
 * (pane coordinates; the pane's centre when omitted) where it was.
 */
export function zoomAbout(
  scroll: { left: number; top: number },
  pane: { width: number; height: number },
  ratio: number,
  anchor?: { x: number; y: number },
): { left: number; top: number } {
  const ax = anchor?.x ?? pane.width / 2;
  const ay = anchor?.y ?? pane.height / 2;
  return {
    left: Math.max(0, Math.round((scroll.left + ax) * ratio - ax)),
    top: Math.max(0, Math.round((scroll.top + ay) * ratio - ay)),
  };
}

/** The sheet's width at "Fit": the whole sheet inside the pane, less a gutter. */
export function fitWidth(paneWidth: number, paneHeight: number, ratio: number, gutter: number) {
  const byWidth = paneWidth - gutter;
  const byHeight = paneHeight > 0 ? (paneHeight - gutter) * ratio : byWidth;
  return Math.max(240, Math.round(Math.min(byWidth, byHeight)));
}
